import React, { useCallback, useMemo, useRef, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { api } from '../../services/api';
import type { ApiError, GamificationShareCard } from '../../types/auth';
import type { ShareSurface } from '../../services/shareAnalytics';
import { GamificationShareModal, type GamificationShareLoadStatus } from './GamificationShareModal';
import {
  buildGamificationShareUrl,
  buildGamificationShareViewModel,
  buildShareCopy,
  type GamificationShareType,
  type GamificationShareViewModel,
  type ShareCopy,
} from './shareCard';

interface Options {
  type: GamificationShareType;
  id: string;
  fallbackMessage?: string;
  surface?: ShareSurface;
}

interface ReadyShare {
  card: GamificationShareCard;
  progressCard: GamificationShareCard | null;
  model: GamificationShareViewModel;
  copy: ShareCopy;
  /** CORE-966 — the token link, known only once the card has loaded. */
  url: string;
}

const errorStatus = (error: unknown): number | undefined => (
  error && typeof error === 'object' && typeof (error as ApiError).status === 'number'
    ? (error as ApiError).status
    : undefined
);

/**
 * CORE-709 — one stateful controller behind every gamification share trigger.
 * It never fabricates an eligible card: 404 is an explicit unavailable state,
 * contract drift is an error, and stale responses cannot win after close/reopen.
 */
export function useGamificationShare({ type, id, fallbackMessage, surface = 'light_path' }: Options) {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [status, setStatus] = useState<GamificationShareLoadStatus>('loading');
  const [ready, setReady] = useState<ReadyShare | null>(null);
  const requestId = useRef(0);

  // CORE-966 — the share link is a property of the CARD (its token), so it is
  // not knowable until the card loads. The profile link stands in until then, so
  // the modal is never rendered without a usable URL.
  const fallbackUrl = buildGamificationShareUrl(user?.id);
  const userId = user?.id;
  const displayName = user?.resolvedDisplayName ?? null;

  const fallbackCopy = useMemo(() => buildShareCopy({
    type,
    card: null,
    fallbackMessage,
    displayName,
    url: fallbackUrl,
  }), [displayName, fallbackMessage, type, fallbackUrl]);

  const load = useCallback(async () => {
    const currentRequest = ++requestId.current;
    setStatus('loading');
    setReady(null);
    try {
      const primaryPromise = type === 'progress'
        ? api.getCurrentGamificationProgressShare()
        : api.getGamificationShareCard(type, id);
      // The level DTO contains the stable level order, not current XP. The
      // current progress endpoint supplies real XP for the approved value line;
      // it is auxiliary and cannot make an otherwise valid level unshareable.
      const progressPromise = type === 'level'
        ? api.getCurrentGamificationProgressShare().catch(() => null)
        : Promise.resolve<GamificationShareCard | null>(null);
      const [card, progressCard] = await Promise.all([primaryPromise, progressPromise]);
      const model = buildGamificationShareViewModel({
        requestedType: type,
        card,
        progressCard,
        fallbackDisplayName: displayName,
      });
      // A backend that predates CORE-966 sends no token; the fallback link keeps
      // sharing working, only without the personal preview.
      const shareUrl = buildGamificationShareUrl(userId, card.shareToken);
      const copy = buildShareCopy({
        type,
        card,
        progressCard,
        fallbackMessage,
        displayName,
        url: shareUrl,
      });
      if (requestId.current !== currentRequest) return;
      setReady({ card, progressCard, model, copy, url: shareUrl });
      setStatus('ready');
    } catch (error) {
      if (requestId.current !== currentRequest) return;
      setReady(null);
      setStatus(errorStatus(error) === 404 ? 'unavailable' : 'error');
    }
  }, [displayName, fallbackMessage, id, type, userId]);

  const openShare = useCallback(() => {
    setOpen(true);
    void load();
  }, [load]);

  const closeShare = useCallback(() => {
    requestId.current += 1;
    setOpen(false);
  }, []);

  const copy = ready?.copy ?? fallbackCopy;
  const modal = (
    <GamificationShareModal
      visible={open}
      onClose={closeShare}
      status={status}
      model={ready?.model ?? null}
      url={ready?.url ?? fallbackUrl}
      message={copy.message}
      subject={copy.subject}
      onRetry={() => { void load(); }}
      analytics={{ entityType: 'gamification', entityKey: type, surface }}
    />
  );

  return { openShare, closeShare, modal, status };
}
